/**
 * SKU Parser — single source of truth for ThreadHeads SKU semantics.
 *
 * Ported verbatim from the print-agent's local copy (Threadheads.SKU NuGet
 * package v2.3.4). Backend (`lib/storage/lookup.ts`), Phase A backfill script,
 * the new product-metadata-sync module, and the print-agent itself ALL import
 * from this module. Drift is a build break.
 *
 * SKU Format (V2 — used by AutoPrint):
 *   4 segments: ProductType-Color-DesignID-PrintID                     (e.g. TB-NT-KO151-S2)
 *   5 segments: ProductType-Size-Color-DesignID-PrintID                (e.g. HD-M-WH-GA2-R8)
 *   6 segments: ProductType-Gender-Size-Color-DesignID-PrintID         (e.g. TEE-M-SM-BK-SR12-R3)
 *
 * SKU Format (V3 — adds PrintPosition as final segment):
 *   5 segments: ProductType-Color-DesignID-PrintID-PrintPosition
 *   6 segments: ProductType-Size-Color-DesignID-PrintID-PrintPosition
 *   7 segments: ProductType-Gender-Size-Color-DesignID-PrintID-PrintPosition
 *
 * Print file format: {DesignID}-{PrintID}-{ColorCode}.arx4
 *   ColorCode is mapped from the SKU color code + product type
 *   (standard vs hoodie vs eco).
 *
 * Dual print: ProductType starts with "2" (V2) OR PrintPosition === "2" (V3)
 *   → front and back print.
 * Back print only: ProductType starts with "1" → back print only (V3).
 *
 * IMPORTANT: This module's outputs are part of the operator-visible file
 * resolution path. Any change must be accompanied by a fixture update in
 * test-fixture.ts and verified against actual Phase A backfilled rows.
 */
// ============================================================================
// Colour Mapper — Port of SKUColourMapper.cs
// ============================================================================
/** Standard garment color → print color code (tees, tanks, long sleeves, etc.) */
export function mapColorCode(skuColor) {
    switch (skuColor) {
        case 'WH':
        case 'NT':
        case 'BR':
        case 'RR':
            return 'CO';
        case 'NY':
        case 'BKST':
        case 'CR':
            return 'N';
        case 'BK':
            return 'B';
        default:
            return 'M';
    }
}
/** Eco-mode color code */
export function mapEcoColorCode(skuColor) {
    switch (skuColor) {
        case 'WH':
        case 'NT':
        case 'BR':
        case 'RR':
            return 'CO';
        default:
            return 'EB';
    }
}
/** Hoodie/sweater color → print color code */
export function mapHoodieColorCode(skuColor) {
    switch (skuColor) {
        case 'WH':
        case 'NT':
        case 'BR':
        case 'RR':
            return 'CO';
        case 'BK':
            return 'BH';
        case 'CR':
            return 'CH';
        default:
            return 'CH';
    }
}
/** Product types that use hoodie color mapping */
export const HOODIE_TYPES = new Set(['HD', 'SWT', 'CSWT', 'KHD', 'KSWT', 'TB']);
// ============================================================================
// SKU Parser — Port of SKUTranslator.Parse()
// ============================================================================
/**
 * Parse a ThreadHeads SKU code into its components.
 *
 * Supports V2 (4-6 segments) and V3 (5-7 segments with PrintPosition).
 * Auto-detects version: 7 segments → V3, otherwise V2.
 */
export function parseSKU(skuCode) {
    if (!skuCode || typeof skuCode !== 'string') {
        throw new Error('Invalid SKU: empty or not a string');
    }
    // Defensive overall length bound (BLU-432 Phase B review finding #2).
    // ThreadHeads SKUs are bounded by Bootleg's encoding conventions to
    // ~25 chars in practice. A 128-char cap is generous (5× observed max)
    // while preventing an attacker-controlled or corrupted upstream from
    // producing multi-KB SKU strings that would propagate into
    // `print_file_lookups.print_filename`. Applies to both standard and
    // CUST-prefixed SKUs (the CUST path uses the full skuCode as designId).
    if (skuCode.length > 128) {
        throw new Error(`Invalid SKU: total length exceeds 128 characters in "${skuCode.slice(0, 32)}..."`);
    }
    // Custom products bypass standard parsing.
    // Print file lookup uses artwork barcode (not SKU), so designId is just
    // the raw SKU.
    if (skuCode.startsWith('CUST')) {
        return {
            skuCode,
            sku: {
                productType: 'CUST',
                gender: '',
                size: '',
                color: '',
                designId: skuCode,
                printId: '',
                printPosition: '',
                isFrontAndBack: false,
            },
            isDualPrint: false,
            isCustom: true,
        };
    }
    const parts = skuCode.split('-');
    // Defensive segment-length bound (BLU-432 Phase B review finding #2).
    // ThreadHeads SKU segments (productType, color, designId, printId, etc.)
    // are bounded by Bootleg's encoding conventions to 1-4 chars in practice.
    // A 32-char cap is generous (8× observed max) while preventing an
    // attacker-controlled or corrupted upstream from producing oversized
    // segments that would propagate into print_file_lookups.print_filename
    // without a length check at the DB CHECK constraint layer.
    for (const segment of parts) {
        if (segment.length > 32) {
            throw new Error(`Invalid SKU: segment exceeds 32 characters in "${skuCode}"`);
        }
    }
    let sku;
    switch (parts.length) {
        // V2: ProductType-Color-DesignID-PrintID
        case 4:
            sku = {
                productType: parts[0],
                gender: '',
                size: '',
                color: parts[1],
                designId: parts[2],
                printId: parts[3],
                printPosition: '',
                isFrontAndBack: false,
            };
            break;
        // V2: ProductType-Size-Color-DesignID-PrintID
        case 5:
            sku = {
                productType: parts[0],
                gender: '',
                size: parts[1],
                color: parts[2],
                designId: parts[3],
                printId: parts[4],
                printPosition: '',
                isFrontAndBack: false,
            };
            break;
        // V2: ProductType-Gender-Size-Color-DesignID-PrintID
        case 6:
            sku = {
                productType: parts[0],
                gender: parts[1],
                size: parts[2],
                color: parts[3],
                designId: parts[4],
                printId: parts[5],
                printPosition: '',
                isFrontAndBack: false,
            };
            break;
        // V3: ProductType-Gender-Size-Color-DesignID-PrintID-PrintPosition
        case 7:
            sku = {
                productType: parts[0],
                gender: parts[1],
                size: parts[2],
                color: parts[3],
                designId: parts[4],
                printId: parts[5],
                printPosition: parts[6],
                isFrontAndBack: parts[6] === '2',
            };
            break;
        default:
            throw new Error(`Invalid SKU format: expected 4-7 segments separated by hyphens, got ${parts.length} in "${skuCode}"`);
    }
    // Dual print: ProductType starts with "2" OR PrintPosition is "2" (V3)
    const isDualPrint = sku.productType.startsWith('2') || sku.isFrontAndBack;
    return {
        skuCode,
        sku,
        isDualPrint,
        isCustom: false,
    };
}
// ============================================================================
// Print File Name — Port of SKUTranslator.ToPrintFile()
// ============================================================================
/**
 * Convert a parsed SKU to its print file name.
 * Format: {DesignID}-{PrintID}-{ColorCode}
 *
 * Color code is determined by product type:
 *   - Hoodies/sweaters (HD, SWT, CSWT, KHD, KSWT, TB) → hoodie color mapping
 *   - Eco mode → eco color mapping
 *   - Everything else → standard color mapping
 *
 * @param sku - Parsed SKU object
 * @param isEcoMode - Use eco color codes (default false)
 * @param isSecondPrint - For V3 dual prints, generates "{DesignID}.1-{PrintID}-{ColorCode}"
 */
export function toPrintFile(sku, isEcoMode = false, isSecondPrint = false) {
    let colorCode;
    if (isEcoMode) {
        colorCode = mapEcoColorCode(sku.color);
    }
    else if (HOODIE_TYPES.has(sku.productType)) {
        colorCode = mapHoodieColorCode(sku.color);
    }
    else {
        colorCode = mapColorCode(sku.color);
    }
    return toPrintFileWithCode(sku, colorCode, isSecondPrint);
}
/**
 * Compose a print file name from an ALREADY-RESOLVED colour code.
 * Format: {DesignID}-{PrintID}-{ColorCode}
 *
 * This is the composition tail of `toPrintFile`, split out so callers that
 * resolve the colour code themselves — e.g. through the per-org
 * `printFileCodeFor` map in `data/rip-routing.ts` — compose the SAME string
 * without re-implementing the design/print/second-print assembly.
 *
 * `colorCode` MUST come from `printFileCodeFor` (map hit, or its built-in
 * fallback) — never a hand-picked or independently-derived code, or a
 * per-org override silently stops applying at this call site.
 *
 * KNOWN NON-PARTICIPANT: `apps/print-agent/src/azure-lookup.ts` keeps its own
 * local `sku-parser.ts` copy and calls `toPrintFile`/`skuToPrintFile` directly
 * on the legacy Azure path — it has no per-org routing map and never goes
 * through `printFileCodeFor` or this function. That is a known, accepted gap
 * (the Azure backend is on the BLU-454 retirement track), not a bug to fix by
 * routing this module's exports through it.
 *
 * @param sku - Parsed SKU object (only designId + printId are read)
 * @param colorCode - The resolved colour code (per-org map hit, or the built-in rule)
 * @param isSecondPrint - For V3 dual prints, generates "{DesignID}.1-{PrintID}-{ColorCode}"
 */
export function toPrintFileWithCode(sku, colorCode, isSecondPrint = false) {
    const designPart = isSecondPrint ? `${sku.designId}.1` : sku.designId;
    return `${designPart}-${sku.printId}-${colorCode}`;
}
/**
 * Convert a SKU code string directly to its print file name.
 */
export function skuToPrintFile(skuCode, isEcoMode = false) {
    const parsed = parseSKU(skuCode);
    return toPrintFile(parsed.sku, isEcoMode);
}
// ============================================================================
// Print File Name with .arx4 Extension
// ============================================================================
/**
 * Convert a print file base name to the .arx4 filename used in blob storage.
 */
export function toPrintFileName(baseFileName) {
    if (baseFileName.endsWith('.arx4')) {
        return baseFileName;
    }
    return `${baseFileName}.arx4`;
}
